import React from 'react'

import { type BreakdownSelection } from 'features/charts-v2/components/breakdown-selection-utils'
import { CustomHeatmap } from 'features/charts-v2/components/CustomHeatmap'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import {
  BREAKDOWNS,
  getMetricById,
  METRICS,
  type MetricType,
} from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

export class HeatmapChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.HEATMAP
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = [
    METRICS.SENTIMENT.id,
    METRICS.RATING.id,
    METRICS.RECORD_COUNT.id,
    METRICS.SENTENCE_COUNT.id,
  ]

  public transform(props: ChartProps): TransformedChartData {
    const augmentedMetrics = [...props.selectedMetrics]

    // Use the first selected metric as primary (respecting user's selection order)
    // Default to Sentiment if no metrics are selected
    const primaryMetric = augmentedMetrics[0] || METRICS.SENTIMENT.id

    // Ensure primary metric is present
    if (!augmentedMetrics.includes(primaryMetric)) {
      augmentedMetrics.unshift(primaryMetric)
    }

    // Determine secondary metric (first non-primary metric)
    const secondaryMetric = augmentedMetrics.find((m) => m !== primaryMetric)

    // Add appropriate paired secondary metric only if one isn't already selected
    if (!secondaryMetric) {
      // Rating pairs with Records, Sentiment pairs with Sentences
      const defaultSecondary =
        primaryMetric === METRICS.RATING.id
          ? METRICS.RECORD_COUNT.id
          : METRICS.SENTENCE_COUNT.id
      augmentedMetrics.push(defaultSecondary)
    }

    return dataTransformationService.transformForHeatmap(
      props.data!,
      augmentedMetrics,
      primaryMetric,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      hoveredDataPoint,
      onDataPointHover,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      xAxisOverflow = 'expand',
      yAxisOverflow = 'expand',
      selectedBreakdowns = [],
      selectedMetrics = [],
      scaleVolume = false,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try selecting a breakdown with two dimensions.',
      )
    }

    const rows = (transformedData.metadata?.['rows'] as string[]) || []
    const columns = (transformedData.metadata?.['columns'] as string[]) || []

    // Get breakdown labels for axis labels
    // First breakdown (selectedBreakdowns[0]) goes to X-axis (columns)
    // Second breakdown (selectedBreakdowns[1]) goes to Y-axis (rows)
    const yAxisLabel = this.getBreakdownLabel(selectedBreakdowns[1]) || 'Row'
    const xAxisLabel = this.getBreakdownLabel(selectedBreakdowns[0]) || 'Column'

    // Determine primary metric (first selected metric, default to Sentiment)
    const primaryMetric = selectedMetrics[0] || METRICS.SENTIMENT.id
    const primaryMetricConfig = getMetricById(primaryMetric)
    const metricLabel = primaryMetricConfig?.label || 'Sentiment'

    // Determine secondary metric (first non-primary metric)
    const secondaryMetric = selectedMetrics.find((m) => m !== primaryMetric)

    // Default paired secondary metric if none selected
    const effectiveSecondaryMetric =
      secondaryMetric ||
      (primaryMetric === METRICS.RATING.id
        ? METRICS.RECORD_COUNT.id
        : METRICS.SENTENCE_COUNT.id)

    const secondaryMetricConfig = getMetricById(effectiveSecondaryMetric)
    const secondaryMetricLabel = secondaryMetricConfig?.label || 'Sentences'

    return (
      <CustomHeatmap
        heatmapData={
          transformedData.chartData as unknown as Array<{
            row: string
            column: string
            primaryMetricValue: number
            sentiment: number
            recordCount: number
            secondaryMetricValue: number
            pctVolumeRow: number
            pctVolumeColumn: number
            color: string
          }>
        }
        rows={rows}
        columns={columns}
        yAxisLabel={yAxisLabel}
        xAxisLabel={xAxisLabel}
        metricLabel={metricLabel}
        secondaryMetricLabel={secondaryMetricLabel}
        primaryMetricId={primaryMetric}
        secondaryMetricId={effectiveSecondaryMetric}
        onDataPointHover={onDataPointHover}
        hoveredDataPoint={hoveredDataPoint}
        fontSizePx={fontSizePx}
        showXAxisLabel={showXAxisLabel}
        showYAxisLabel={showYAxisLabel}
        xAxisOverflow={xAxisOverflow}
        yAxisOverflow={yAxisOverflow}
        scaleVolume={scaleVolume}
        onSegmentClick={
          onSegmentClick
            ? (segmentData, event) => {
                // CustomHeatmap names the cell axes as primaryBreakdown=row
                // (Y) and stackingBreakdown=column (X). Heatmap renders
                // selectedBreakdowns[1] on Y and selectedBreakdowns[0] on X
                // (see getTitle below), so swap before bubbling up — the
                // rest of the codebase expects primaryBreakdown to be the
                // value for selectedBreakdowns[0].
                onSegmentClick(
                  {
                    primaryBreakdown: segmentData.stackingBreakdown ?? '',
                    stackingBreakdown: segmentData.primaryBreakdown,
                    value: segmentData.value,
                    payload: segmentData.payload as Record<
                      string,
                      string | number | boolean | null
                    >,
                  },
                  event,
                )
              }
            : undefined
        }
      />
    )
  }

  private getBreakdownLabel(breakdown: BreakdownSelection | undefined): string {
    if (!breakdown) return 'Unknown'

    if (typeof breakdown === 'string') {
      // Standard breakdown - look up label
      const breakdownConfig = Object.values(BREAKDOWNS).find(
        (b) => b.id === breakdown,
      )
      return breakdownConfig?.label || breakdown
    }

    // Custom breakdown - use alias
    if (breakdown.kind === 'custom') {
      return breakdown.alias || breakdown.field
    }

    // Standard breakdown object
    if (breakdown.kind === 'standard') {
      const breakdownConfig = Object.values(BREAKDOWNS).find(
        (b) => b.id === breakdown.id,
      )
      return breakdownConfig?.label || breakdown.id
    }

    return 'Unknown'
  }

  public getTitle(props?: ChartProps): string {
    if (!props?.selectedBreakdowns || props.selectedBreakdowns.length < 2) {
      return 'Heatmap'
    }

    const yAxisLabel = this.getBreakdownLabel(props.selectedBreakdowns[1])
    const xAxisLabel = this.getBreakdownLabel(props.selectedBreakdowns[0])

    return `${yAxisLabel} vs. ${xAxisLabel} Heatmap`
  }
}
